// TPP Core: icon motion (pairs with tpp-icons.css). Presentation only.
// One-time entrances for the small icons across the site, run by IntersectionObserver as they scroll in:
//   line icons draw on (stroke-dashoffset), image icons fade/rise/settle, the HSA/FSA tick wipes in.
// Icons that are already on screen when this runs are left alone (never hide what someone can already see).
// prefers-reduced-motion: reduce (or no IntersectionObserver) = nothing is armed, everything stays static.
// Content injected later (theme editor, app widgets, AJAX-rendered cards) is picked up by a MutationObserver.
(function () {
  if (window.__tppIcons) return;
  window.__tppIcons = true;
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Line icons (inline SVG strokes): trust tiles on PDPs, multi-column / icon rows from snippets/icon.liquid.
  var DRAW = [
    '#MainContent svg.icon--stroke-only'
  ].join(',');
  // Image icons (PNG): homepage USP row, PDP benefit lists, feature accordions, Kiwi "how to use" discs, Kiwi feature discs.
  var POP = [
    '.ct-usp__icon',
    '.tpp-pdp__liquid img',
    '.features-accordion__drawer-header img',
    '.ct-features__drawer-header img',
    '.ct-fv__icon-img',
    '.template-product .logo-list__logo-image img'
  ].join(',');
  // HSA/FSA badge on cards and PDPs: the tick is the first path.
  var CHECK = '.fsa-icon svg';
  var SHAPES = 'path, line, polyline, polygon, circle, rect, ellipse';
  var POP_MAX = 120;    // px: anything bigger is an image, not an icon
  var BASE_DELAY = 120; // ms: lands just after the section/card reveal starts
  var STEP = 80;        // ms between icons that enter together
  var DRAW_MS = 850, POP_MS = 700, CHECK_MS = 550;

  var vh = function () { return window.innerHeight || document.documentElement.clientHeight; };
  var onScreen = function (el) {
    var r = el.getBoundingClientRect();
    if (!r.width && !r.height) return false; // not rendered yet (closed drawer, hidden breakpoint): arm it
    return r.top < vh() && r.bottom > 0;
  };

  var cleanup = function (el, kind, ms) {
    setTimeout(function () {
      if (kind === 'draw') {
        el.querySelectorAll(SHAPES).forEach(function (s) {
          s.style.removeProperty('stroke-dasharray');
          s.style.removeProperty('stroke-dashoffset');
        });
        el.classList.remove('tpp-ic-draw');
      } else if (kind === 'pop') {
        el.classList.remove('tpp-ic-pop', 'is-in');
      } else {
        el.classList.remove('tpp-ic-check', 'is-in');
      }
      el.style.removeProperty('--tpp-ic-d');
    }, ms + 150);
  };

  var play = function (el, delay) {
    var kind = el.__tppIc;
    el.style.setProperty('--tpp-ic-d', delay + 'ms');
    // Next frame so the armed state has been painted and the transition runs.
    requestAnimationFrame(function () {
      if (kind === 'draw') {
        el.querySelectorAll(SHAPES).forEach(function (s) { if (s.style.strokeDashoffset) s.style.strokeDashoffset = '0'; });
        cleanup(el, kind, DRAW_MS + delay);
      } else {
        el.classList.add('is-in');
        cleanup(el, kind, (kind === 'pop' ? POP_MS : CHECK_MS) + delay);
      }
    });
  };

  var io = new IntersectionObserver(function (entries) {
    var i = 0;
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      play(e.target, BASE_DELAY + Math.min(i++, 5) * STEP);
    });
  // Fire once about a third of the icon is on screen: early enough that its label is never seen without it,
  // late enough that the motion happens where it can be seen (also for cards half-in at a rail's edge).
  }, { threshold: 0.35 });

  var armDraw = function (svg) {
    var shapes = svg.querySelectorAll(SHAPES), lens = [];
    try {
      for (var k = 0; k < shapes.length; k++) {
        var len = shapes[k].getTotalLength();
        if (!(len > 0)) return false;
        lens.push(len);
      }
    } catch (err) { return false; }
    if (!lens.length) return false;
    var sw = parseFloat(getComputedStyle(svg).strokeWidth) || 2;
    shapes.forEach(function (s, k) {
      // Dash covers the path, the gap is longer than the path, and the offset clears the round cap.
      s.style.strokeDasharray = lens[k] + ' ' + (lens[k] + sw * 3);
      s.style.strokeDashoffset = String(lens[k] + sw);
    });
    svg.classList.add('tpp-ic-draw');
    return true;
  };

  var arm = function (el, kind) {
    if (el.__tppIc !== undefined) return;
    el.__tppIc = null;
    if (el.closest('header, .section-header, [data-section-type="header"], .no-tpp-ic')) return;
    if (onScreen(el)) return;
    if (kind === 'pop') {
      var w = el.getBoundingClientRect().width;
      if (w > POP_MAX) return;
      // Leave Boost's own scroll animation alone where it's running.
      if (document.documentElement.classList.contains('cc-animate-enabled') && el.closest('[data-cc-animate]')) return;
      el.classList.add('tpp-ic-pop');
    } else if (kind === 'draw') {
      if (!armDraw(el)) return;
    } else {
      el.classList.add('tpp-ic-check');
    }
    el.__tppIc = kind;
    io.observe(el);
  };

  var scan = function (root) {
    if (!root.querySelectorAll) return;
    var each = function (sel, kind) {
      if (root.matches && root.matches(sel)) arm(root, kind);
      root.querySelectorAll(sel).forEach(function (el) { arm(el, kind); });
    };
    each(DRAW, 'draw');
    each(POP, 'pop');
    each(CHECK, 'check');
  };

  var start = function () {
    scan(document);
    var queue = [], pending = false;
    new MutationObserver(function (records) {
      records.forEach(function (r) { r.addedNodes.forEach(function (n) { if (n.nodeType === 1) queue.push(n); }); });
      if (pending || !queue.length) return;
      pending = true;
      requestAnimationFrame(function () {
        var nodes = queue; queue = []; pending = false;
        nodes.forEach(function (n) { if (n.isConnected) scan(n); });
      });
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
